import React, { useState, useRef } from 'react';
import MaerskButton from '../common/MaerskButton';
import JsonDisplay from '../common/JsonDisplay';
import { McCheckbox } from '@maersk-global/mds-react-wrapper/components-core/mc-checkbox';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';
import { McModal } from '@maersk-global/mds-react-wrapper/components-core/mc-modal';
import { McNotification } from '@maersk-global/mds-react-wrapper/components-core/mc-notification';
import { McSelectNative } from '@maersk-global/mds-react-wrapper/components-core/mc-select-native';
import { COLORS, SPACING, BORDER_RADIUS } from '../../constants/theme';
import {
  PLAYGROUND_EXTRACTION_METHODS,
  PLAYGROUND_TEST_MODEL_OPTIONS,
} from '../../constants/playgroundOptions';
import api from '../../services/api';
import { analyzeDocument } from '../../services/azureDocumentService';

const TestSystemMessage = ({ systemMessage, responseFormat, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentText, setDocumentText] = useState('');
  const [selectedModels, setSelectedModels] = useState([PLAYGROUND_TEST_MODEL_OPTIONS[0].id]);
  const [extractionMethod, setExtractionMethod] = useState(PLAYGROUND_EXTRACTION_METHODS[0]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setDocumentText('');
      setTestResults([]);
      setError('');
    }
  };

  const isTxtFile = selectedFile?.name?.toLowerCase().endsWith('.txt');

  const handleExtractDocument = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsExtracting(true);
    setError('');

    // .txt files are read directly in the browser — no backend round-trip needed
    if (isTxtFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result || '';
        setDocumentText(text);
        setIsExtracting(false);
        if (!text.trim()) {
          setError('The text file appears to be empty');
        }
      };
      reader.onerror = () => {
        setError('Failed to read the text file');
        setIsExtracting(false);
      };
      reader.readAsText(selectedFile, 'UTF-8');
      return;
    }

    try {
      console.log('Starting document extraction...');
      const result = await analyzeDocument(selectedFile, extractionMethod);
      console.log('Extraction result:', result);
      const extractedText = typeof result === 'string'
        ? result
        : (result?.content || result?.text || '');

      console.log('Content length:', extractedText?.length);
      setDocumentText(extractedText);
      console.log('Document text set successfully');

      if (!extractedText || !extractedText.trim()) {
        setError('Document extraction completed but no text content was found');
      }
    } catch (err) {
      setError(`Failed to extract document: ${err.message}`);
      console.error('Document extraction error:', err);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleModelToggle = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        // Don't allow deselecting all models
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };

  const handleTestLLMs = async () => {
    if (!documentText) {
      setError('Please extract a document first');
      return;
    }

    if (selectedModels.length === 0) {
      setError('Please select at least one model');
      return;
    }

    setIsTesting(true);
    setError('');
    setTestResults([]);

    try {
      const response = await api.post('/system-messages/test-multiple', {
          documentText,
          systemMessage,
          responseFormat,
          models: selectedModels,
          temperature: 0.5,
          reasoningEffort: 'low'
        });

      setTestResults(response.data.results);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to test LLMs';
      setError(`Testing failed: ${msg}`);
      console.error('LLM testing error:', err);
    } finally {
      setIsTesting(false);
    }
  };

  // McSelectNative reports the picked option(s) in its change event detail.
  const handleExtractionMethodChange = (e) => {
    const picked = e.detail?.selectedOptions?.[0];
    if (picked !== undefined) setExtractionMethod(typeof picked === 'object' ? picked.value : picked);
  };

  const sectionLabel = (text) => (
    <div className="mds-text--small-bold" style={{ marginBottom: SPACING.xs, color: COLORS.text }}>{text}</div>
  );

  return (
    <McModal open heading="Test Instruction" width="95vw" height="90vh" padding="none" closed={onClose}>
        {/* Content */}
        <div style={{
          display: 'flex',
          height: '100%',
          minHeight: '70vh',
          overflow: 'hidden'
        }}>
          {/* Left Panel - Setup */}
          <div style={{
            width: '350px',
            flexShrink: 0,
            borderRight: `1px solid ${COLORS.borderLight}`,
            padding: SPACING.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.lg,
            overflowY: 'auto'
          }}>
            {/* File Upload */}
            <div>
              {sectionLabel('Document')}
              {/* Hidden native file input: opened from the drop zone below. */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.pdf,.xlsx,.xls,.xlsm,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div style={{
                border: `2px dashed ${COLORS.border}`,
                borderRadius: BORDER_RADIUS.md,
                padding: SPACING.md,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: selectedFile ? COLORS.backgroundLight : COLORS.white
              }}
              onClick={() => fileInputRef.current?.click()}>
                {selectedFile ? (
                  <div>
                    <div style={{ marginBottom: SPACING.xs }}>
                      <McIcon icon="file-check" size="24" color={COLORS.success} />
                    </div>
                    <div className="mds-text--small-normal" style={{ color: COLORS.text, wordBreak: 'break-word' }}>
                      {selectedFile.name}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: SPACING.xs }}>
                      <McIcon icon="tray-arrow-up" size="24" color={COLORS.textSecondary} />
                    </div>
                    <div className="mds-text--small-normal" style={{ color: COLORS.textSecondary }}>
                      Click to upload
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Extraction Method — hidden for plain text files */}
            {!isTxtFile && (
              <McSelectNative
                label="Extraction Method"
                options={PLAYGROUND_EXTRACTION_METHODS}
                value={[extractionMethod]}
                change={handleExtractionMethodChange}
                disabled={isExtracting}
              />
            )}

            <MaerskButton
              onClick={handleExtractDocument}
              loading={isExtracting}
              style={{ width: '100%' }}
            >
              {isExtracting
                ? (isTxtFile ? 'Reading...' : 'Extracting...')
                : (isTxtFile ? 'Read File' : 'Extract Document')}
            </MaerskButton>

            {/* Extraction Success Indicator */}
            {documentText && !isExtracting && (
              <McNotification appearance="success" fit="small" icon="check-circle" heading="Document extracted successfully">
                {documentText.length.toLocaleString()} characters
              </McNotification>
            )}

            {/* Model Selection */}
            <div>
              {sectionLabel('Select Models')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
                {PLAYGROUND_TEST_MODEL_OPTIONS.map(model => {
                  const checked = selectedModels.includes(model.id);
                  return (
                    <div
                      key={model.id}
                      style={{
                        padding: SPACING.sm,
                        border: `1px solid ${checked ? COLORS.primary : COLORS.border}`,
                        borderRadius: BORDER_RADIUS.md,
                        backgroundColor: checked ? COLORS.primaryWeak : COLORS.white,
                        transition: 'all 0.2s'
                      }}
                    >
                      <McCheckbox
                        label={model.name}
                        hint={model.description}
                        checked={checked}
                        change={(e) => {
                          // At least one model stays selected; undo the box's own toggle in that case.
                          if (checked && selectedModels.length === 1) {
                            e.target.checked = true;
                            return;
                          }
                          handleModelToggle(model.id);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Settings Disclaimer */}
            <McNotification appearance="info" fit="small">
              <strong>Note:</strong> Tests use temperature=0.5 for GPT 4.1 and reasoning_effort=low for GPT 5 models
            </McNotification>

            <MaerskButton
              onClick={handleTestLLMs}
              disabled={!documentText?.trim() || selectedModels.length === 0 || isTesting}
              loading={isTesting}
              style={{ width: '100%' }}
            >
              {isTesting ? `Testing ${selectedModels.length} Model${selectedModels.length > 1 ? 's' : ''}...` : `Test ${selectedModels.length} Model${selectedModels.length > 1 ? 's' : ''}`}
            </MaerskButton>

            {error && (
              <McNotification appearance="error" fit="small">{error}</McNotification>
            )}
          </div>

          {/* Right Panel - Results */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {testResults.length === 0 ? (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: COLORS.textSecondary,
                padding: SPACING.xl
              }}>
                <div style={{ marginBottom: SPACING.md }}>
                  <McIcon icon="chemistry-beakers" size="24" color={COLORS.textSecondary} />
                </div>
                <p className="mds-text--large-bold" style={{ margin: 0 }}>
                  Ready to Test
                </p>
                <p className="mds-text--small-normal" style={{ margin: `${SPACING.xs}px 0 0 0` }}>
                  Upload a document, select models, and click "Test" to see results
                </p>
              </div>
            ) : (
              <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(testResults.length, 2)}, 1fr)`,
                gap: SPACING.md,
                padding: SPACING.lg,
                overflowY: 'auto'
              }}>
                {testResults.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      border: `1px solid ${result.success ? COLORS.success : COLORS.error}`,
                      borderRadius: BORDER_RADIUS.md,
                      padding: SPACING.md,
                      backgroundColor: COLORS.white,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0
                    }}
                  >
                    {/* Model Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: SPACING.sm,
                      paddingBottom: SPACING.sm,
                      borderBottom: `1px solid ${COLORS.borderLight}`
                    }}>
                      <div>
                        <div className="mds-text--medium-bold" style={{ color: COLORS.text }}>
                          {result.model}
                        </div>
                        <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary, marginTop: 2 }}>
                          {result.duration}s
                        </div>
                      </div>
                      <McIcon
                        icon={result.success ? 'check-circle' : 'times-circle'}
                        color={result.success ? COLORS.success : COLORS.error}
                      />
                    </div>

                    {/* Response Content */}
                    <div className="mds-text--small-normal" style={{
                      flex: 1,
                      overflowY: 'auto'
                    }}>
                      {result.success ? (
                        <div style={{
                          backgroundColor: COLORS.white,
                          padding: SPACING.sm,
                          borderRadius: BORDER_RADIUS.sm,
                          border: `1px solid ${COLORS.border}`
                        }}>
                          <JsonDisplay data={result.response} />
                        </div>
                      ) : (
                        <McNotification appearance="error" fit="small">{result.error}</McNotification>
                      )}
                      {result.warning && (
                        <div style={{ marginTop: SPACING.xs }}>
                          <McNotification appearance="warning" fit="small">{result.warning}</McNotification>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
    </McModal>
  );
};

export default TestSystemMessage;
